import cx from "classnames";
import Lottie from "lottie-light-react";
import React from "react";
import { PAYOFF_ANIMATIONS } from "../../NationalDogSurvey/fixtures";
import WrappedSection from "../../containers/WrappedSection/WrappedSection";
import ParagraphSupportWidget from "../../containers/paragraphs/ParagraphSupportWidget/ParagraphSupportWidget";
import SectionArticleOrEventCards from "../../containers/sections/SectionArticleOrEventCards";
import { SectionFuzzyBorderBottom } from "../../context/PageManager/PageManager.Borders";
import PostCodeLotteryLogo from "../../static/nds/img/PPL_logo_2022_RGB.png";
import { Colours } from "../../utils/colours";
import { Button, SocialMediaIcons } from "../common";
import * as styles from "./NDSThankYou.module.scss";

interface TestProps {
	title: string;
	subTitle: string;
}

interface NDSWhatHappensNextProps {
	body: string;
}

interface NDSSocialMediaProps {
	share: {
		shareSectionTitle: string;
		shareSectionBody: string;
		shareText: string;
		xShareUrl: string;
		fbShareUrl: string;
		emailShareUrl: string;
		whatsappShareUrl: string;
	};
}

type NDSRelatedReadingProps = {
	title: string;
	bodyHtmlText: string;
	relatedContentItems: DogsTrust.SignPostCardProps[];
};

const embedCode = `<script src="https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/4.3.6/iframeResizer.min.js"></script>
<iframe src="https://support.dogstrust.org.uk/widget/DFLNQ/DWVBv" frameborder="0" style="width: 100%; background: #fff; box-shadow: 0 0 10px rgba(0, 0, 0, 0.5); border-radius: 10px;" id="dt-donation-widget" title="NDS thank you donation"></iframe>
<script>
  window.addEventListener('message', function(msg) {
	msg.data.iFrameConfig = {
	  ref: window.location.href
	}
	var iframe = document.getElementById('dt-donation-widget').contentWindow;
	iframe.postMessage(
	  {"iFrameConfig":msg.data.iFrameConfig},
	  "https://support.dogstrust.org.uk/widget/DFLNQ/DWVBv");
	iFrameResize({ log: false }, '#dt-donation-widget');
  });
</script>`;
export const NDSThankYouHeader: React.FC<TestProps> = ({ title, subTitle }) => {
	return (
		<>
			<header className={styles.ndsThankYouTemplateHeader}>
				<h1 className={styles.ndsThankYouTemplateHeaderTitle}>{title}</h1>
				<h2>{subTitle}</h2>
			</header>
			<SectionFuzzyBorderBottom
				borderColour={Colours.WHITE}
				nextSection={{
					primaryColour: Colours.SUNSHINE_YELLOW,
					sectionId: "NDSIntroLottie",
					sectionType: "intro",
				}}
			/>
		</>
	);
};

export const NDSIntroLottie: React.FC<{
	image: DogsTrust.MediaImageProps & { lottieFile?: boolean };
}> = ({ image }) => {
	return (
		image && (
			<div className={styles.ndsThankYouTemplateImage} aria-hidden>
				{!image.lottieFile && (
					<img src={image.src} alt={image.alt} aria-hidden={!image.alt} />
				)}
				{image.lottieFile && (
					<Lottie
						className={cx(styles.lottieAnimation)}
						animationData={PAYOFF_ANIMATIONS.GLOBAL.END_OF_SURVEY}
						loop={true}
						aria-hidden
					/>
				)}
			</div>
		)
	);
};

export const NDSWhatHappensNext: React.FC<NDSWhatHappensNextProps> = ({
	body,
}) => {
	return (
		<WrappedSection
			sectionName="NDSWhatHappensNext"
			sectionColour={Colours.MIDNIGHT}
		>
			{body && (
				<div
					className={styles.thankYouTemplateBody}
					// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
					dangerouslySetInnerHTML={{ __html: body }}
				/>
			)}
		</WrappedSection>
	);
};

export const NDSDonateNow = () => {
	return (
		<WrappedSection
			sectionName="NDSDonateNow"
			sectionColour={Colours.SANDY_FUR}
			className={styles.thankYouDonateNow}
		>
			<div className={styles.thankYouTemplateBody}>
				<h2>Donate now</h2>
				<p>
					Your kind donations help us provide life changing care and forever
					homes for over <strong>14,000</strong> of our furry friends. We really
					appreciate your support (and we know the dogs do too!)
				</p>
			</div>
			<ParagraphSupportWidget
				embedCode={embedCode}
				theme={"primary"}
				sectionColour={Colours.SANDY_FUR}
			/>
		</WrappedSection>
	);
};

export const NDSPostcodeLotteryFindOutMore = () => {
	return (
		<WrappedSection
			sectionName="NDSPostcodeLotterySignup"
			sectionColour={Colours.SUNSHINE_YELLOW}
			className={styles.thankYouPostcodeLotterySignup}
		>
			<div className={styles.thankYouPostcodeLotterySignupWrapper}>
				<h2 className={styles.thankYouPostcodeLotterySignupTitle}>
					Did you know...
					<span className={styles.thankYouPostcodeLotterySignupSmallText}>
						that we've done amazing things for our four-legged friends, thanks
						to players of People's Postcode Lottery who raise money to support
						Dogs Trust and other charities?
					</span>
				</h2>
				<div className={styles.thankYouPostcodeLotterySignupButtonWrapper}>
					<p>
						Find out how Dogs Trust and other charities are supported by
						People's Postcode Lottery
					</p>
					<Button
						className={styles.thankYouPostcodeLotterySignupButton}
						variant="primary"
						type="submit"
						ariaLabel="submit"
						size="small"
						fullWidth={true}
						url={
							"https://www.postcodelottery.co.uk/dogstrustsurvey?utm_source=dogs-trust-survey&utm_medium=email&utm_campaign=prospect-lead-gen"
						}
					>
						Find out more
					</Button>
				</div>
				<img
					src={PostCodeLotteryLogo}
					className={styles.thankYouPostcodeLotterySignupImage}
					aria-hidden="true"
					alt=""
				/>
			</div>
		</WrappedSection>
	);
};

export const NDSSocialMedia: React.FC<NDSSocialMediaProps> = ({ share }) => {
	return (
		<WrappedSection
			sectionName="ArticlePageSocialMedia"
			sectionColour={Colours.SUNSHINE_YELLOW}
		>
			<div
				className={cx(styles.thankYouSocialSharingContainer, styles.shareText)}
			>
				<h2>{share.shareSectionTitle}</h2>
				{share.shareSectionBody && (
					<div
						className={styles.socialSharingText}
						// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
						dangerouslySetInnerHTML={{ __html: share.shareSectionBody }}
					/>
				)}
				<div className={styles.socialSharing}>
					<SocialMediaIcons
						invert={true}
						xUrl={`https://twitter.com/intent/tweet?url=${share.xShareUrl}&via=DogsTrust&text=${share.shareText} ${share.xShareUrl}`}
						facebookUrl={`https://www.facebook.com/sharer/sharer.php?u=${share.fbShareUrl}`}
						mailToUrl={`mailto:?subject=I saw this on the Dogs Trust’s website...&body=${share.shareText} ${share.emailShareUrl}`}
						whatsAppUrl={`https://wa.me/?text=${share.shareText} ${share.whatsappShareUrl}`}
					/>
				</div>
			</div>
		</WrappedSection>
	);
};

export const NDSRelatedReading: React.FC<NDSRelatedReadingProps> = ({
	title,
	bodyHtmlText,
	relatedContentItems,
}) => {
	return (
		<WrappedSection
			sectionName="ArticlePageSocialMedia"
			sectionColour={Colours.WHITE}
		>
			<SectionArticleOrEventCards
				items={relatedContentItems}
				title={title}
				bodyHtmlText={bodyHtmlText}
			/>
		</WrappedSection>
	);
};
